import {Component, OnDestroy, OnInit} from '@angular/core';
import {Page} from '../../../../model/page';
import {PaginationInstance} from 'ngx-pagination';
import {MatDialog} from '@angular/material/dialog';
import {MFilterModalComponent} from '../../components/filter-modal/m-filter-modal.component';
import {SelectOption} from '../../model/SelectOption';
import {MenuItem} from 'primeng/api';
import {ActivatedRoute, ActivatedRouteSnapshot, Router} from '@angular/router';
import {AuthService} from '../../../../auth/auth.service';
import {MasterDataService} from '../../../../views/oes/services/master-data.service';
import {Subscription} from 'rxjs/Subscription';
import {ToastrService} from 'ngx-toastr';
import cols from './m-master-data.cols';
import {Title} from '@angular/platform-browser';

@Component({
    selector: 'app-m-master-data',
    templateUrl: './m-master-data.component.html',
    styleUrls: ['./m-master-data.component.scss'],
    providers: [
        AuthService,
        MasterDataService
    ]
})
export class MMasterDataComponent implements OnInit, OnDestroy {

    public page: Page;
    public config: PaginationInstance;
    private size: number;
    private totalRecords: number;

    public isWorkspaceSelected: boolean;
    public loading: boolean;

    public items: Array<any>;
    public contextMenu: Array<MenuItem>;
    private selected: any;
    private subscription: Subscription;

    private availableFilters: {[key: string]: {label: string, type: number}};
    private filterOptions: {[key: string]: Array<SelectOption>};
    private sortOptions: Array<SelectOption>;
    public appliedFilters: Array<string>;

    constructor(
        public dialog: MatDialog,
        private router: Router,
        private authService: AuthService,
        private route: ActivatedRoute,
        private masterDataService: MasterDataService,
        private toast: ToastrService,
        private title: Title
    ) {
        this.title.setTitle('OES - Master Data');
    }

    ngOnInit() {
        this.isWorkspaceSelected = this.getSelectedWorkspace();

        this.initValues();
        this.setConfig();

        const filterParams = this.route.snapshot.queryParams;
        this.parseParams( filterParams );
        this.populateAppliedFilters();

        if (this.isWorkspaceSelected) {
            this.getData(false);
        }
    }

    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    getData(reloadWithParams = true) {
        this.setPage({offset: this.page.pageNumber}, reloadWithParams);
    }

    getSelectedWorkspace() {
        return this.authService.workspaceMasterCustomerID !== null;
    }

    onFilter() {
        const filterDialog = this.dialog.open(MFilterModalComponent, {
            width: '90%',
            maxWidth: (window.innerWidth - 40) + 'px',
            role: 'dialog',
            hasBackdrop: true,
            disableClose: true,
            data: {
                filters: this.availableFilters,
                filterParams: this.page.filterParams,
                filterOptions: this.filterOptions,
                enableSort: true,
                sortColumn: this.page.filterParams.hasOwnProperty('SORT_COLUMN') ? this.page.filterParams['SORT_COLUMN'] : '',
                sortDirection: this.page.filterParams.hasOwnProperty('SORT_DIRECTION') ? this.page.filterParams['SORT_DIRECTION'] : 'ASC',
                sortColumnOptions: this.sortOptions
            }
        });

        const filterSubscription = filterDialog.componentInstance.onApply.subscribe(result => {
            this.loading = true;
            this.page.filterParams = result;
            this.page.pageNumber = 0;
            this.getData();

            filterSubscription.unsubscribe();
        });
    }

    onOpenContextMenu(event, cm, rowData) {
        this.selected = rowData;

        event.preventDefault();
        event.stopPropagation();

        cm.show(event);
        return false;
    }

    onPage(event) {
        this.loading = true;
        this.size = event.rowsPerPage;
        this.page.size = event.rowsPerPage;
        this.page.pageNumber = event.page;
        this.page.filterParams['perPage'] = event.rowsPerPage;
        this.page.filterParams['page'] = event.page;
        this.getData();
    }

    onReload() {
        this.getData(false);
    }

    setConfig() {
        this.config = {
            id: 'custom',
            itemsPerPage: this.size,
            currentPage: Number(this.page.pageNumber) + 1,
            totalItems: this.totalRecords
        };
    }

    private setPage(pageInfo, reloadWithParams = true) {
        if (reloadWithParams) {
            this.page.filterParams['page'] = pageInfo.offset ? pageInfo.offset : 0;
            this.page.filterParams['perPage'] = pageInfo.size ? pageInfo.size : this.size;
            this.router.routeReuseStrategy.shouldReuseRoute = function (future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot) {
                return false;
            }
            this.router.navigate([], { relativeTo: this.route, queryParams: this.page.filterParams });
        } else {
            this.loading = true;
            this.subscription = this.masterDataService.getDataList(this.page).subscribe(result => {
                if (result.hasOwnProperty('data') && result.hasOwnProperty('count')) {
                    this.totalRecords = result.count;
                    this.items = result.data;
                    this.setConfig();
                    this.loading = false;
                } else {
                    this.toast.error('Unexpected error in loading data.', 'Error', {
                        timeOut: 3000,
                    });
                }
            }, () => {
                this.loading = false;
                this.toast.error('Request timed out.', 'Service Error', {
                    timeOut: 3000,
                });
            });
        }
    }

    private initValues() {
        // initialize variables
        this.page = new Page();
        this.size = 20;
        this.totalRecords = 0;
        this.page.pageNumber = 0;
        this.page.size = 20;
        this.items = [];
        this.loading = false;

        // define operations/actions supported per row item
        this.contextMenu = [
            {label: 'View', icon: 'pi pi-fw pi-search', command: () => this.onView()}
        ];

        // define filter fields, sort options, and data items to populate any select filter fields
        const columns = cols;
        this.availableFilters = {};
        columns.forEach(column => {
            if (column.filter) {
                this.availableFilters[column.field] = {label: column.label, type: column.type};
            }
        });
        this.sortOptions = columns.filter(column => column.sort).map(column => {
            return {value: column.field, label: column.label}
        });
        this.sortOptions.unshift({value: null, label: 'None'});
        this.filterOptions = {};
    }

    private onView() {
        if (this.selected) {
            this.router.navigate(['mobile/oes/master-data/view/' + this.selected['MD1_ID']]);
        }
    }

    private parseParams( filterParams ) {
        if (filterParams !== undefined && filterParams !== null) {
            for (const key of Object.keys(filterParams)) {
                const value = filterParams[key];
                if (value && value.toString() !== '') {
                    if (key.toString() === 'page') {
                        this.page.pageNumber = Number(value);
                    } else if (key.toString() === 'perPage') {
                        this.page.size = Number(value);
                        this.size = Number(value);
                    } else {
                        this.page.filterParams[key] = value;
                    }
                }
            }
        }
    }

    private populateAppliedFilters() {
        this.appliedFilters = [];
        for (const key of Object.keys(this.availableFilters)) {
            if (this.page.filterParams.hasOwnProperty(key) && this.page.filterParams[key] !== null && this.page.filterParams[key] !== '') {
                this.appliedFilters.push(this.availableFilters[key].label);
            }
        }
        if (this.page.filterParams.hasOwnProperty('SORT_COLUMN') && this.page.filterParams.hasOwnProperty('SORT_DIRECTION')) {
            if (
                this.page.filterParams['SORT_COLUMN'] !== '' && this.page.filterParams['SORT_DIRECTION'] !== ''
                && this.availableFilters.hasOwnProperty(this.page.filterParams['SORT_COLUMN'])
            ) {
                this.appliedFilters.push(
                    this.availableFilters[this.page.filterParams['SORT_COLUMN']].label
                    + ' - ' + this.page.filterParams['SORT_DIRECTION']
                );
            }
        }
    }

}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/modules/mobile/views/master-data/m-master-data.component.ts